import React, {useEffect, useState} from "react";
import "./Header.css";
import {HamburgerMenu, Logo} from "../../assets/images";
import SideMenu from "./SideMenu/SideMenu";
import {useLocation, useNavigate, Link} from "react-router-dom";
import {useScrollY} from "../../context/ScrollContext";

const sectionIds = ["home", "service", "projects", "testimonial", "about"];

const routeMap = {
  service: ["/services"],
  projects: ["/projects"],
  testimonial: ["/testimonials"],
  about: ["/about-me"],
};

const Header = ({scrollRef}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollY = useScrollY();

  const [menuOpen, setMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);

  // Sticky Header on scroll
  useEffect(() => {
    setIsSticky(scrollY > 50);
  }, [scrollY]);

  // Scroll to section on home
  const handleScrollLink = (id) => {
    if (location.pathname === "/") {
      scrollRef?.current?.scrollToId(id);
    } else {
      navigate("/", {state: {scrollTarget: id}});
    }
  };

  // Track active section using IntersectionObserver
  useEffect(() => {
    if (location.pathname !== "/") return;

    const options = {
      root: null,
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    const timeoutId = setTimeout(() => {
      sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [location.pathname]);

  // Backup sticky header scroll
  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Utility: Check if item should be active

  const isActive = (id) => {
    const path = location.pathname;
    if (path === "/" && activeSection === id) return true;
    if (path === `/${id}`) return true;
    if (routeMap[id]?.includes(path)) return true;
    if (id === "projects" && path.startsWith("/projects/")) return true;
    return false;
  };

  return (
    <header
      className={`header header-height ${isSticky ? "header-sticky" : ""}`}
    >
      <div className="header-area header-transparent">
        <div className="header-container container">
          <div className="p-relative">
            <div className="header-wrapper">
              {/* Logo */}
              <a
                onClick={() => handleScrollLink("home")}
                className="logo-section"
              >
                <img src={Logo} alt="Logo" />
              </a>

              {/* Main Menu */}
              <div className="header-main-menu">
                <nav className="wt-main-menu-content">
                  <ul className="wt-onepage-menu">
                    <li className={isActive("home") ? "active" : ""}>
                      <a
                        onClick={() => handleScrollLink("home")}
                        className="nav-link-btn"
                      >
                        Home
                      </a>
                    </li>
                    <li className={isActive("service") ? "active" : ""}>
                      <a
                        onClick={() => handleScrollLink("service")}
                        className="nav-link-btn"
                      >
                        Services
                      </a>
                    </li>
                    <li className={isActive("projects") ? "active" : ""}>
                      <a
                        onClick={() => handleScrollLink("projects")}
                        className="nav-link-btn"
                      >
                        Projects
                      </a>
                    </li>
                    <li className={isActive("testimonial") ? "active" : ""}>
                      <a
                        onClick={() => handleScrollLink("testimonial")}
                        className="nav-link-btn"
                      >
                        Testimonial
                      </a>
                    </li>
                    <li className={isActive("about") ? "active" : ""}>
                      <a
                        onClick={() => handleScrollLink("about")}
                        className="nav-link-btn"
                      >
                        About
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>

              {/* Inquiries Button */}
              <div className="inq-header-button icon_main">
                <Link to="/contact-us" className="inq-header-btn">
                  Inquiries
                  <span className="icon_box">
                    <i className="icon_first fa-regular fa-arrow-right"></i>
                    <i className="icon_second fa-regular fa-arrow-right"></i>
                  </span>
                </Link>
              </div>

              {/* Hamburger Toggle */}
              <div className="header-menu-toggle d-lg-none">
                <button className="wt-menu-bar" onClick={toggleMenu}>
                  <img src={HamburgerMenu} alt="HamburgerMenu" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SideMenu
        isOpen={menuOpen}
        closeMenu={closeMenu}
        activeSection={activeSection}
      />
    </header>
  );
};

export default Header;
